import React from "react";
import { Box, Button, Divider, IconButton, LinearProgress, Typography } from "@mui/material";
import {
  CheckCircleOutline,
  Close,
  ErrorOutline,
  ImageOutlined,
  PictureAsPdfOutlined,
  Refresh,
} from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  FILE_UPLOAD_STATUS,
  PROCESSING_STATUS,
  type FileUploadItem,
  type BatchChildStatus,
} from "../types/attachment";
import { V2_COLORS } from "../constants/colors";

// --- Helpers ---

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function isPdf(file: File): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function FileIcon({ file }: { file: File }) {
  if (isPdf(file)) {
    return <PictureAsPdfOutlined sx={{ fontSize: 20, color: V2_COLORS.error, flexShrink: 0 }} />;
  }
  return <ImageOutlined sx={{ fontSize: 20, color: V2_COLORS.primary, flexShrink: 0 }} />;
}

export function isFailedStatus(status?: string): boolean {
  return status === PROCESSING_STATUS.OCR_FAILED || status === PROCESSING_STATUS.VALIDATION_FAILED;
}

export function isSuccessStatus(status?: string): boolean {
  return (
    status === PROCESSING_STATUS.OCR_COMPLETED ||
    status === PROCESSING_STATUS.ALREADY_PROCESSED ||
    status === PROCESSING_STATUS.VALIDATION_SUCCESS
  );
}

export function processingLabel(status: string): { text: string; color: string } {
  switch (status) {
    case PROCESSING_STATUS.OCR_IN_PROGRESS:
      return { text: "OCR In Progress", color: V2_COLORS.textSecondary };
    case PROCESSING_STATUS.OCR_COMPLETED:
      return { text: "OCR Complete", color: V2_COLORS.success };
    case PROCESSING_STATUS.ALREADY_PROCESSED:
      return { text: "Already Processed", color: V2_COLORS.success };
    case PROCESSING_STATUS.VALIDATION_IN_PROGRESS:
      return { text: "Validation In Progress", color: V2_COLORS.textSecondary };
    case PROCESSING_STATUS.VALIDATION_SUCCESS:
      return { text: "Validation Passed", color: V2_COLORS.success };
    case PROCESSING_STATUS.OCR_FAILED:
      return { text: "OCR Failed", color: V2_COLORS.error };
    case PROCESSING_STATUS.VALIDATION_FAILED:
      return { text: "Validation Failed", color: V2_COLORS.error };
    default:
      return { text: "Processing...", color: V2_COLORS.textSecondary };
  }
}

// --- Row components per phase ---

interface RowProps {
  item: FileUploadItem;
  index: number;
}

interface SelectedRowProps extends RowProps {
  onRemove: (index: number) => void;
}

const SelectedRow = React.memo(function SelectedRow({ item, index, onRemove }: SelectedRowProps) {
  return (
    <Box display="flex" alignItems="center" gap={1.5} py={1} px={1.5}>
      <FileIcon file={item.file} />
      <Box flex={1} minWidth={0}>
        <Typography fontSize="13px" fontWeight={500} noWrap>
          {item.file.name}
        </Typography>
        <Typography fontSize="11px" color={V2_COLORS.textSecondary}>
          {formatFileSize(item.file.size)}
        </Typography>
      </Box>
      <IconButton size="small" onClick={() => onRemove(index)}>
        <Close sx={{ fontSize: 16, color: V2_COLORS.textSecondary }} />
      </IconButton>
    </Box>
  );
});

const UploadingRow = React.memo(function UploadingRow({ item }: RowProps) {
  const isActive =
    item.status === FILE_UPLOAD_STATUS.UPLOADING || item.status === FILE_UPLOAD_STATUS.CREATING;

  return (
    <Box display="flex" alignItems="center" gap={1.5} py={1} px={1.5}>
      <FileIcon file={item.file} />
      <Box flex={1} minWidth={0}>
        <Typography fontSize="13px" fontWeight={500} noWrap>
          {item.file.name}
        </Typography>
        {isActive && (
          <LinearProgress
            variant="determinate"
            value={item.progress}
            sx={{ mt: 0.5, height: 3, borderRadius: 2 }}
          />
        )}
      </Box>
      <Typography fontSize="12px" color={V2_COLORS.textSecondary} sx={{ minWidth: 32, textAlign: "right" }}>
        {item.progress}%
      </Typography>
    </Box>
  );
});

interface ProcessingRowProps extends RowProps {
  childStatus?: string;
  childError?: string;
}

const ProcessingRow = React.memo(function ProcessingRow({ item, childStatus, childError }: ProcessingRowProps) {
  const isFailed = isFailedStatus(childStatus);
  const isSuccess = isSuccessStatus(childStatus);
  const label = childStatus ? processingLabel(childStatus) : { text: "Processing...", color: V2_COLORS.textSecondary };

  return (
    <Box py={1} px={1.5}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <FileIcon file={item.file} />
        <Typography fontSize="13px" fontWeight={500} noWrap flex={1} minWidth={0}>
          {item.file.name}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          {isFailed ? (
            <ErrorOutline sx={{ fontSize: 14, color: V2_COLORS.error }} />
          ) : isSuccess ? (
            <CheckCircleOutline sx={{ fontSize: 14, color: V2_COLORS.success }} />
          ) : (
            <CircularProgress size={14} sx={{ color: V2_COLORS.primary }} />
          )}
          <Typography fontSize="12px" color={label.color}>
            {label.text}
          </Typography>
        </Box>
      </Box>
      {isFailed && childError && (
        <Typography fontSize="11px" color={V2_COLORS.error} pl={4.25} mt={0.25} noWrap title={childError}>
          {childError}
        </Typography>
      )}
    </Box>
  );
});

interface CompleteRowProps extends RowProps {
  onRetry?: (index: number) => void;
}

const SuccessRow = React.memo(function SuccessRow({ item, childStatus, childError, batchError }: RowProps & { childStatus?: string; childError?: string; batchError?: boolean }) {
  const isFailed = isFailedStatus(childStatus);
  const label = batchError
    ? { text: "OCR Pending", color: V2_COLORS.textSecondary }
    : childStatus
      ? processingLabel(childStatus)
      : { text: "Uploaded", color: V2_COLORS.success };

  return (
    <Box py={1} px={1.5}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <CheckCircleOutline sx={{ fontSize: 20, color: V2_COLORS.success, flexShrink: 0 }} />
        <Typography fontSize="13px" fontWeight={500} noWrap flex={1} minWidth={0}>
          {item.file.name}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          {batchError ? (
            <ErrorOutline sx={{ fontSize: 14, color: V2_COLORS.textSecondary }} />
          ) : isFailed ? (
            <ErrorOutline sx={{ fontSize: 14, color: V2_COLORS.error }} />
          ) : (
            <CheckCircleOutline sx={{ fontSize: 14, color: V2_COLORS.success }} />
          )}
          <Typography fontSize="12px" color={label.color}>
            {label.text}
          </Typography>
        </Box>
      </Box>
      {isFailed && childError && (
        <Typography fontSize="11px" color={V2_COLORS.error} pl={4.25} mt={0.25} noWrap title={childError}>
          {childError}
        </Typography>
      )}
    </Box>
  );
});

const FailedRow = React.memo(function FailedRow({ item, index, onRetry }: CompleteRowProps) {
  const isRetrying =
    item.status === FILE_UPLOAD_STATUS.QUEUED ||
    item.status === FILE_UPLOAD_STATUS.CREATING ||
    item.status === FILE_UPLOAD_STATUS.UPLOADING;

  return (
    <Box py={1} px={1.5}>
      <Box display="flex" alignItems="center" gap={1.5}>
        {/* Fixed 20x20 box so icon swaps don't shift the row */}
        <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {isRetrying ? (
            <CircularProgress size={18} sx={{ color: V2_COLORS.primary }} />
          ) : (
            <ErrorOutline sx={{ fontSize: 20, color: V2_COLORS.error }} />
          )}
        </Box>
        <Typography fontSize="13px" fontWeight={500} noWrap flex={1} minWidth={0}>
          {item.file.name}
        </Typography>
        {/* Fixed-height container so Retry button / progress text swap doesn't shift the row */}
        <Box sx={{ height: 30, display: "flex", alignItems: "center", flexShrink: 0 }}>
          {isRetrying ? (
            <Typography fontSize="12px" color={V2_COLORS.textSecondary}>
              {item.progress}%
            </Typography>
          ) : (
            onRetry && (
              <Button
                size="small"
                startIcon={<Refresh sx={{ fontSize: 14 }} />}
                onClick={() => onRetry(index)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  color: V2_COLORS.primary,
                  minWidth: "auto",
                  px: 1,
                  height: 30,
                }}
              >
                Retry
              </Button>
            )
          )}
        </Box>
      </Box>
      {/* Keep the second line present during retry to maintain row height */}
      <Typography fontSize="11px" color={isRetrying ? V2_COLORS.textSecondary : V2_COLORS.error} pl={4.25} mt={0.25}>
        {isRetrying ? "Retrying..." : item.error || "\u00A0"}
      </Typography>
    </Box>
  );
});

// --- Section header ---

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      fontSize="11px"
      fontWeight={700}
      letterSpacing="0.5px"
      color={V2_COLORS.textSecondary}
      px={1.5}
      pt={1.5}
      pb={0.5}
    >
      {children}
    </Typography>
  );
}

// --- Main component ---

type ModalPhase = "selected" | "uploading" | "processing" | "complete";

interface FileUploadListProps {
  items: FileUploadItem[];
  phase: ModalPhase;
  batchChildren?: BatchChildStatus[];
  /** True when the batch processing API itself failed (e.g. 404) */
  batchError?: boolean;
  onRemoveFile?: (index: number) => void;
  onRetryFile?: (index: number) => void;
}

type ChildEntry = { status: string; reason?: string };

/** Build a lookup map from attachment_id → processing status + failure reason */
function buildChildMap(children?: BatchChildStatus[]): Map<string, ChildEntry> {
  const map = new Map<string, ChildEntry>();
  if (children) {
    for (const child of children) {
      map.set(child.attachment_id, { status: child.status, reason: child.reason });
    }
  }
  return map;
}

export default function FileUploadList({ items, phase, batchChildren, batchError, onRemoveFile, onRetryFile }: FileUploadListProps) {
  if (items.length === 0) return null;

  // Processing phase — show per-file OCR status from polling data
  if (phase === "processing") {
    const childMap = buildChildMap(batchChildren);
    const uploaded = items.filter((i) => i.status === FILE_UPLOAD_STATUS.UPLOADED);

    return (
      <Box>
        <SectionHeader>{uploaded.length} FILE(S) PROCESSING</SectionHeader>
        <Box border={`1px solid ${V2_COLORS.border}`} borderRadius="4px">
          {uploaded.map((item, i) => {
            const child = item.attachmentId ? childMap.get(item.attachmentId) : undefined;
            return (
              <React.Fragment key={item.file.name + i}>
                {i > 0 && <Divider />}
                <ProcessingRow
                  item={item}
                  index={i}
                  childStatus={child?.status}
                  childError={child?.reason}
                />
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  }

  if (phase === "complete") {
    const childMap = buildChildMap(batchChildren);
    const indexed = items.map((item, i) => ({ item, index: i }));
    const succeeded = indexed.filter(({ item }) => item.status === FILE_UPLOAD_STATUS.UPLOADED);
    // "not yet succeeded" = failed + currently retrying — kept in one group so the container stays stable
    const unresolved = indexed.filter(({ item }) => item.status !== FILE_UPLOAD_STATUS.UPLOADED);

    return (
      <Box>
        {succeeded.length > 0 && (
          <>
            <SectionHeader>
              {succeeded.length} UPLOADED SUCCESSFULLY
            </SectionHeader>
            <Box border={`1px solid ${V2_COLORS.border}`} borderRadius="4px">
              {succeeded.map(({ item, index }, i) => {
                const child = item.attachmentId ? childMap.get(item.attachmentId) : undefined;
                return (
                  <React.Fragment key={item.file.name + index}>
                    {i > 0 && <Divider />}
                    <SuccessRow
                      item={item}
                      index={index}
                      childStatus={child?.status}
                      childError={child?.reason}
                      batchError={batchError}
                    />
                  </React.Fragment>
                );
              })}
            </Box>
          </>
        )}

        {unresolved.length > 0 && (
          <>
            <SectionHeader>
              {unresolved.length} FAILED &mdash; ACTION REQUIRED
            </SectionHeader>
            <Box border={`1px solid ${V2_COLORS.error}`} borderRadius="4px" sx={{ backgroundColor: V2_COLORS.errorBg }}>
              {unresolved.map(({ item, index }, i) => (
                <React.Fragment key={item.file.name + index}>
                  {i > 0 && <Divider sx={{ borderColor: "rgba(219,56,42,0.2)" }} />}
                  <FailedRow item={item} index={index} onRetry={onRetryFile} />
                </React.Fragment>
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  }

  // "selected" and "uploading" phases render a flat list
  return (
    <Box>
      {phase === "selected" && (
        <SectionHeader>{items.length} FILE(S) SELECTED</SectionHeader>
      )}
      <Box border={`1px solid ${V2_COLORS.border}`} borderRadius="4px">
        {items.map((item, i) => (
          <React.Fragment key={item.file.name + i}>
            {i > 0 && <Divider />}
            {phase === "selected" ? (
              <SelectedRow item={item} index={i} onRemove={onRemoveFile!} />
            ) : (
              <UploadingRow item={item} index={i} />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
